import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {BenchlingState} from 'redux-stores/reduceApp';
import {createFulfillmentDefinitions} from 'requests/execution/CreateFulfillmentDefinitionUtil';
import {
  FulfillmentStatus,
  REQUEST_FULFILLMENT_DEFINITION_SHAPE,
  RequestFulfillmentDefinition,
} from 'requests/fulfillments/FulfillmentConstants';
import {fulfillmentMatchesDefinition} from 'requests/fulfillments/FulfillmentUtils';
import {Request, REQUEST_SHAPE, RequestSchema} from 'requests/RequestConstants';
import {
  REQUEST_TASK_SHAPE,
  RequestTask,
  RequestTaskSchema,
  SCHEMA_ID_REQUEST_TASK_SCHEMA_ALL,
} from 'requests/tasks/RequestTaskConstants';
import RequestTaskSchemaSelector from 'requests/tasks/RequestTaskSchemaSelector';
import {Filter, FILTER_SHAPE, FILTERS} from 'search/SearchConstants';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import SampleGroupTable from './SampleGroupTable';

interface BaseSelectSamplesPaneProps {
  requests: Array<Request>;
  requestSchemasByRequestId: {[requestId: string]: RequestSchema};
  statusFilter: FulfillmentStatus | null;
  initialFilters: Array<Filter>;
  onGoToNextPane: () => void;
  onRequestClose: () => void;
  onSetSelectedFulfillmentDefinitions: (fulfillmentDefinitions: Array<RequestFulfillmentDefinition>) => void;
  onSetStatusFilter: (statusFilter: FulfillmentStatus) => void;
  titlePrefix: string;
}

interface ConnectedProps {
  fulfillmentDefinitions: Array<RequestFulfillmentDefinition> | null;
  requestTasks?: Array<RequestTask>;
  selectedFulfillmentDefinitions: Array<RequestFulfillmentDefinition> | null;
}

interface State {
  selectedTaskSchemaId: string | null;
}

class SelectSamplesPane extends React.Component<BaseSelectSamplesPaneProps & ConnectedProps, State> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    requests: PropTypes.arrayOf(REQUEST_SHAPE.isRequired).isRequired,
    requestSchemasByRequestId: PropTypes.object.isRequired,
    statusFilter: PropTypes.oneOf(Object.values(FulfillmentStatus)),
    initialFilters: PropTypes.arrayOf(FILTER_SHAPE.isRequired).isRequired,

    // Props provided by connect
    fulfillmentDefinitions: PropTypes.arrayOf(REQUEST_FULFILLMENT_DEFINITION_SHAPE.isRequired),
    requestTasks: PropTypes.arrayOf(REQUEST_TASK_SHAPE.isRequired).isRequired,
    selectedFulfillmentDefinitions: PropTypes.arrayOf(REQUEST_FULFILLMENT_DEFINITION_SHAPE.isRequired),

    onGoToNextPane: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    onSetSelectedFulfillmentDefinitions: PropTypes.func.isRequired,
    onSetStatusFilter: PropTypes.func.isRequired,
  };

  state: State = {
    selectedTaskSchemaId: null,
  };

  componentDidMount(): void {
    if (this.props.selectedFulfillmentDefinitions != null) {
      this.setState({
        selectedTaskSchemaId: this._getInitialSelectedTaskSchemaId(),
      });
    }
  }

  componentDidUpdate(prevProps: BaseSelectSamplesPaneProps & ConnectedProps, prevState: State): void {
    if (
      prevProps.selectedFulfillmentDefinitions == null &&
      this.props.selectedFulfillmentDefinitions != null
    ) {
      this.setState({
        selectedTaskSchemaId: this._getInitialSelectedTaskSchemaId(),
      });
    } else if (prevState.selectedTaskSchemaId !== this.state.selectedTaskSchemaId) {
      this._filterSelectionByTaskId();
    }
  }

  _getInitialSelectedTaskSchemaId(): string | null {
    const taskSchemaFilter = this.props.initialFilters.find(
      (filter) => filter.key === FILTERS.REQUEST_TASK_SCHEMA.key
    );
    const selectedTaskSchemaId = taskSchemaFilter == null ? null : taskSchemaFilter.value;

    return selectedTaskSchemaId as string | null;
  }

  _filterSelectionByTaskId(): void {
    if (
      this.state.selectedTaskSchemaId == null ||
      this.state.selectedTaskSchemaId === SCHEMA_ID_REQUEST_TASK_SCHEMA_ALL
    ) {
      return;
    }
    const selectedTaskIds = new Set(
      this.props
        .requestTasks!.filter((requestTask) => requestTask.schemaId === this.state.selectedTaskSchemaId)
        .map((requestTask) => requestTask.id)
    );
    assertAndContinue(
      selectedTaskIds.size > 0,
      'Selected task schema is expected to match at least one task on the request(s)'
    );
    const validSelectedFulfillmentDefinitions = this.props.selectedFulfillmentDefinitions!.filter(
      (fulfillmentDefinition) =>
        fulfillmentDefinition.requestTaskId != null &&
        selectedTaskIds.has(fulfillmentDefinition.requestTaskId)
    );
    this.props.onSetSelectedFulfillmentDefinitions(validSelectedFulfillmentDefinitions);
  }

  _handleTaskSchemaSelected = (selectedTaskSchema: RequestTaskSchema): void => {
    const selectedTaskSchemaId = selectedTaskSchema != null ? selectedTaskSchema.id : null;
    this.setState({selectedTaskSchemaId});
  };

  _isLoading(): boolean {
    return this.props.selectedFulfillmentDefinitions == null;
  }

  _renderTaskSchemaSelector(): JSX.Element {
    return (
      <FormGroup>
        <Label>Task</Label>
        <RequestTaskSchemaSelector
          requestTasks={this.props.requestTasks!}
          selectedTaskSchemaId={this.state.selectedTaskSchemaId}
          onSelectSchema={this._handleTaskSchemaSelected}
        />
      </FormGroup>
    );
  }

  render(): JSX.Element {
    return (
      <Modal
        buttons={[
          {
            text: 'Next',
            key: 'next',
            style: ButtonStyle.PRIMARY,
            disabled: this._isLoading() || this.props.selectedFulfillmentDefinitions!.length === 0,
            onClick: this.props.onGoToNextPane,
            dataTestAttrs: {
              component: 'SelectSamplesPane',
              element: 'footerButton',
              key: ModalButtonKey.NEXT,
            },
          },
        ]}
        scrollable={true}
        large={true}
        expandHeight={true}
        onRequestClose={this.props.onRequestClose}
        title={`${this.props.titlePrefix}${
          this.props.requestTasks!.length ? 'Select task and samples' : 'Select samples'
        }`}
      >
        {this._isLoading() ? (
          <CenteredHorizontalThrobber />
        ) : (
          <div>
            {this.props.requestTasks!.length ? this._renderTaskSchemaSelector() : null}
            <SampleGroupTable
              // eslint-disable-next-line no-restricted-syntax
              requestIds={_.pluck(this.props.requests, 'id')}
              fulfillmentDefinitions={this.props.fulfillmentDefinitions!}
              selectedFulfillmentDefinitions={this.props.selectedFulfillmentDefinitions!}
              statusFilter={this.props.statusFilter}
              selectedTaskSchemaId={this.state.selectedTaskSchemaId}
              requestSchemasByRequestId={this.props.requestSchemasByRequestId}
              onSetStatusFilter={this.props.onSetStatusFilter}
              onSetSelectedFulfillmentDefinitions={this.props.onSetSelectedFulfillmentDefinitions}
              dataTestAttrs={{
                component: 'SelectSamplesPane',
                element: 'SampleGroupTable',
              }}
            />
          </div>
        )}
      </Modal>
    );
  }
}

export function mapStateToProps(state: BenchlingState, props: BaseSelectSamplesPaneProps): ConnectedProps {
  const {
    requestTaskIdsByRequestId,
    requestTasksById,
    sampleGroupIdsByRequestId,
    sampleGroupIdsByRequestTaskId,
  }: {
    requestTaskIdsByRequestId: {[requestId: string]: Array<string>};
    requestTasksById: {[taskId: string]: RequestTask};
    sampleGroupIdsByRequestId: {[requestId: string]: Array<string>};
    sampleGroupIdsByRequestTaskId: {[requestTaskId: string]: Array<string>};
  } = state.models.requests.inputs;
  const {fulfillmentIdsByRequestId} = state.models.requests.fulfillments;

  if (
    sampleGroupIdsByRequestTaskId == null ||
    props.requests.some(
      ({id}) =>
        fulfillmentIdsByRequestId[id] == null ||
        requestTaskIdsByRequestId[id] == null ||
        sampleGroupIdsByRequestId[id] == null
    )
  ) {
    return {
      fulfillmentDefinitions: null,
      selectedFulfillmentDefinitions: null,
    };
  }

  let requestTasks: Array<RequestTask> = [];
  for (const request of props.requests) {
    const tasksForRequest = requestTaskIdsByRequestId[request.id].map(
      (requestTaskId) => requestTasksById[requestTaskId]
    );
    requestTasks = requestTasks.concat(tasksForRequest);
  }

  const fulfillmentDefinitions = createFulfillmentDefinitions({
    requests: props.requests,
    requestTaskIdsByRequestId,
    sampleGroupIdsByRequestId,
    sampleGroupIdsByRequestTaskId,
  });

  let {selectedFulfillmentDefinitions} = state.ui.requests.executeRequestFlow;
  const {fulfillmentsById} = state.models.requests.fulfillments;
  const fulfillments = Object.keys(fulfillmentsById).map((fulfillmentId) => fulfillmentsById[fulfillmentId]);

  if (selectedFulfillmentDefinitions == null) {
    if (fulfillmentDefinitions != null) {
      const pendingFulfillmentDefinitions = fulfillmentDefinitions.filter((fulfillmentDefinition) => {
        const matchingFulfillmentIdx = fulfillments.findIndex((fulfillment) =>
          fulfillmentMatchesDefinition(fulfillment, fulfillmentDefinition)
        );
        return (
          matchingFulfillmentIdx < 0 ||
          fulfillments[matchingFulfillmentIdx].status === FulfillmentStatus.PENDING
        );
      });
      selectedFulfillmentDefinitions = pendingFulfillmentDefinitions;
      props.onSetSelectedFulfillmentDefinitions(pendingFulfillmentDefinitions);
    } else {
      selectedFulfillmentDefinitions = [];
    }
  }

  return {
    fulfillmentDefinitions,
    selectedFulfillmentDefinitions,
    requestTasks,
  };
}

export const RawSelectSamplesPane = SelectSamplesPane;

export default connect(mapStateToProps)(SelectSamplesPane);
